import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { api } from "@/lib/api";

const EMPTY = { question: "", answer: "", category: "Général", order: 0, published: true };

const SUGGESTED_CATEGORIES = [
  "Syndic",
  "Syndic en ligne",
  "AG en visioconférence",
  "ASL",
  "Expertise",
  "Général",
];

export default function AdminFaq() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [filterCat, setFilterCat] = useState("Toutes");

  const load = () =>
    api.get("/admin/faq").then((r) => setItems(r.data)).catch(() => setItems([]));

  useEffect(() => {
    load();
  }, []);

  const cats = useMemo(() => {
    const s = new Set(items.map((i) => i.category));
    return ["Toutes", ...Array.from(s).sort()];
  }, [items]);

  const filtered = useMemo(
    () => (filterCat === "Toutes" ? items : items.filter((i) => i.category === filterCat)),
    [items, filterCat]
  );

  const openNew = () => {
    setEditing("new");
    setForm(EMPTY);
  };
  const openEdit = (e) => {
    setEditing(e.id);
    setForm({
      question: e.question,
      answer: e.answer,
      category: e.category,
      order: e.order || 0,
      published: e.published,
    });
  };
  const close = () => {
    setEditing(null);
    setForm(EMPTY);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") {
        await api.post("/admin/faq", form);
        toast.success("Question ajoutée.");
      } else {
        await api.put(`/admin/faq/${editing}`, form);
        toast.success("Question mise à jour.");
      }
      close();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette question ?")) return;
    try {
      await api.delete(`/admin/faq/${id}`);
      toast.success("Question supprimée.");
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <div data-testid="admin-faq">
      <div className="flex items-center justify-between mb-8">
        <div>
          <span className="font-overline text-[#B8935A]">Back-office</span>
          <h1 className="font-serif text-3xl text-[#0F172A] mt-2">FAQ</h1>
          <p className="text-sm text-[#64748B] mt-1">Questions / réponses publiées sur le site.</p>
        </div>
        <button onClick={openNew} data-testid="admin-faq-new" className="gpi-btn-primary">
          <Plus size={16} /> Nouvelle question
        </button>
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        {cats.map((c) => (
          <button
            key={c}
            onClick={() => setFilterCat(c)}
            data-testid={`admin-faq-filter-${c}`}
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border transition-all ${
              filterCat === c
                ? "bg-[#0F172A] border-[#0F172A] text-white"
                : "bg-white border-[#E2E8F0] text-[#475569] hover:border-[#B8935A]"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="bg-white border border-[#E2E8F0]">
        <table className="w-full text-sm">
          <thead className="bg-[#F9F8F6] text-left text-[#64748B] text-xs uppercase tracking-wider">
            <tr>
              <th className="px-5 py-3 w-40">Catégorie</th>
              <th className="px-5 py-3">Question</th>
              <th className="px-5 py-3 w-24">Statut</th>
              <th className="px-5 py-3 w-32 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => (
              <tr key={e.id} className="border-t border-[#E2E8F0]" data-testid={`admin-faq-row-${e.id}`}>
                <td className="px-5 py-4 text-[#475569]">{e.category}</td>
                <td className="px-5 py-4 font-medium text-[#0F172A]">{e.question}</td>
                <td className="px-5 py-4">
                  <span
                    className={`px-2 py-1 text-xs ${
                      e.published ? "bg-[#EFE8DC] text-[#0F172A]" : "bg-[#FEE2E2] text-[#991B1B]"
                    }`}
                  >
                    {e.published ? "Publié" : "Brouillon"}
                  </span>
                </td>
                <td className="px-5 py-4 text-right whitespace-nowrap">
                  <button
                    onClick={() => openEdit(e)}
                    data-testid={`admin-faq-edit-${e.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-[#0F172A] hover:bg-[#EFE8DC] mr-1"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => remove(e.id)}
                    data-testid={`admin-faq-delete-${e.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-[#991B1B] hover:bg-[#FEE2E2]"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={4} className="px-5 py-12 text-center text-[#64748B]">
                  Aucune question.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start lg:items-center justify-center p-4 overflow-auto">
          <form onSubmit={save} className="bg-white w-full max-w-2xl border border-[#E2E8F0] my-8" data-testid="admin-faq-form">
            <div className="flex items-center justify-between p-6 border-b border-[#E2E8F0]">
              <h2 className="font-serif text-xl text-[#0F172A]">
                {editing === "new" ? "Nouvelle question" : "Modifier la question"}
              </h2>
              <button type="button" onClick={close} className="text-[#64748B] hover:text-[#0F172A]">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <Field label="Question" required>
                <input
                  data-testid="faq-form-question"
                  value={form.question}
                  onChange={(ev) => setForm({ ...form, question: ev.target.value })}
                  required
                  className="gpi-form-input"
                />
              </Field>
              <Field label="Réponse (HTML accepté)" required>
                <textarea
                  data-testid="faq-form-answer"
                  rows={8}
                  value={form.answer}
                  onChange={(ev) => setForm({ ...form, answer: ev.target.value })}
                  required
                  className="gpi-form-input"
                />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Catégorie" required>
                  <input
                    data-testid="faq-form-category"
                    list="faq-cats"
                    value={form.category}
                    onChange={(ev) => setForm({ ...form, category: ev.target.value })}
                    required
                    className="gpi-form-input"
                  />
                  <datalist id="faq-cats">
                    {SUGGESTED_CATEGORIES.map((c) => (
                      <option key={c} value={c} />
                    ))}
                  </datalist>
                </Field>
                <Field label="Ordre d'affichage">
                  <input
                    type="number"
                    data-testid="faq-form-order"
                    value={form.order}
                    onChange={(ev) => setForm({ ...form, order: parseInt(ev.target.value, 10) || 0 })}
                    className="gpi-form-input"
                  />
                </Field>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  data-testid="faq-form-published"
                  checked={form.published}
                  onChange={(ev) => setForm({ ...form, published: ev.target.checked })}
                />
                Publié
              </label>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-[#E2E8F0] bg-[#F9F8F6]">
              <button type="button" onClick={close} className="gpi-btn-outline">
                Annuler
              </button>
              <button type="submit" className="gpi-btn-primary" data-testid="admin-faq-save">
                Enregistrer
              </button>
            </div>
          </form>
        </div>
      )}

      <style>{`
        .gpi-form-input {
          width: 100%;
          padding: 0.625rem 1rem;
          border: 1px solid #E2E8F0;
          background: #F9F8F6;
          font-size: 0.9rem;
          outline: none;
          transition: all 0.25s ease;
        }
        .gpi-form-input:focus {
          border-color: #0F172A;
          background: white;
        }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-[#64748B] uppercase tracking-wider mb-2">
        {label} {required && <span className="text-[#B8935A]">*</span>}
      </span>
      {children}
    </label>
  );
}
